import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  updateDoc
} from "firebase/firestore";
import { db } from "@/app/providers/Firebase";
import TourMembership from "@/models/TourMembership";

const TOURS = "tours";
const MEMBERS = "members";

function memberRef(tourId: string, userId: string) {
  return doc(db, TOURS, tourId, MEMBERS, userId);
}

/**
 * Phase 1 client repository. Permission-changing operations will move behind
 * trusted server actions before the public multi-tour rollout.
 */
export const tourMembershipRepository = {
  async getByUserId(tourId: string, userId: string) {
    const snapshot = await getDoc(memberRef(tourId, userId));
    if (!snapshot.exists()) return null;

    return { userId: snapshot.id, ...snapshot.data() } as TourMembership;
  },

  async create(tourId: string, membership: TourMembership) {
    await setDoc(memberRef(tourId, membership.userId), membership);
  },

  async update(
    tourId: string,
    userId: string,
    data: Partial<Omit<TourMembership, "userId">>
  ) {
    await updateDoc(memberRef(tourId, userId), data);
  },

  subscribe(tourId: string, userId: string, callback: (membership: TourMembership | null) => void) {
    return onSnapshot(memberRef(tourId, userId), snapshot => {
      callback(
        snapshot.exists()
          ? ({ userId: snapshot.id, ...snapshot.data() } as TourMembership)
          : null
      );
    });
  },

  subscribeAll(tourId: string, callback: (memberships: TourMembership[]) => void) {
    return onSnapshot(collection(db, TOURS, tourId, MEMBERS), snapshot => {
      callback(snapshot.docs.map(document => ({
        userId: document.id,
        ...document.data()
      } as TourMembership)));
    });
  },

  collection(tourId: string) {
    return collection(db, TOURS, tourId, MEMBERS);
  }
};
